/**
 * O que faz: Transforma regras de campanha e de modelos de follow-up em textos curtos para a tela
 *            (intervalos legíveis, linha do tempo da sequência, gatilho e restrições ativas).
 * Onde usa: CampaignCard, PresetPicker e demais telas da aba Follow-up.
 * Conexões: Tipos de src/types/followup.ts; funções puras, sem React e sem banco.
 * Tabelas: Nenhuma; só formata dados já carregados de follow_up_campaigns e follow_up_templates.
 * Data: 07/10/2026
 */

import type { FollowupCampaign, FollowupTemplate } from '@/types/followup';

const MINUTOS_POR_HORA = 60;
const MINUTOS_POR_DIA = 1440;

/**
 * Converte minutos em texto curto ("15 min", "2 h 45 min", "1 dia", "3 dias e 4 h").
 * Motivo: o formato antigo arredondava ("2 h" para 2 h 45) e escrevia "1 dias".
 */
export const formatarIntervaloFollowup = (minutos: number | null | undefined): string => {
  if (minutos === null || minutos === undefined || !Number.isFinite(minutos) || minutos <= 0) return '0 min';
  const total = Math.round(minutos);
  const dias = Math.floor(total / MINUTOS_POR_DIA);
  const horas = Math.floor((total % MINUTOS_POR_DIA) / MINUTOS_POR_HORA);
  const resto = total % MINUTOS_POR_HORA;

  if (dias > 0) {
    const textoDias = `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
    return horas > 0 ? `${textoDias} e ${horas} h` : textoDias;
  }
  if (horas > 0) return resto > 0 ? `${horas} h ${resto} min` : `${horas} h`;
  return `${resto} min`;
};

export interface PassoLinhaDoTempoFollowup {
  sequencia: number;
  /** Texto do momento do envio: "após 15 min" na 1ª mensagem e "+3 h" nas seguintes. */
  momento: string;
  titulo: string;
  usaIa: boolean;
  temImagem: boolean;
  conteudo: string;
}

/**
 * Monta a linha do tempo da sequência em ordem de envio.
 * Regra de negócio: a 1ª mensagem sai quando o lead completa o tempo sem resposta da campanha;
 * cada mensagem seguinte sai "intervalo_minutos" depois da anterior.
 */
export const montarLinhaDoTempoFollowup = (
  janelaInatividadeMinutos: number,
  templates: Pick<FollowupTemplate, 'sequencia' | 'titulo' | 'intervalo_minutos' | 'gerado_por_ia' | 'imagem_object_key' | 'conteudo'>[] = [],
): PassoLinhaDoTempoFollowup[] => [...templates]
  .sort((primeiro, segundo) => primeiro.sequencia - segundo.sequencia)
  .map((template, indice) => ({
    sequencia: indice + 1,
    momento: indice === 0
      ? `após ${formatarIntervaloFollowup(janelaInatividadeMinutos)}`
      : `+${formatarIntervaloFollowup(template.intervalo_minutos)}`,
    titulo: template.titulo,
    usaIa: Boolean(template.gerado_por_ia),
    temImagem: Boolean(template.imagem_object_key),
    conteudo: template.conteudo ?? '',
  }));

/** Frase única que explica quando a campanha dispara, em linguagem de quem atende. */
export const resumirGatilhoFollowup = (
  campanha: Pick<FollowupCampaign, 'janela_inatividade_minutos' | 'modo_envio' | 'horario_envio' | 'horario_limite_envio' | 'apenas_dias_uteis'>,
): string => {
  const espera = formatarIntervaloFollowup(campanha.janela_inatividade_minutos);
  const inicio = campanha.horario_envio?.slice(0, 5) || '09:00';
  const limite = campanha.horario_limite_envio?.slice(0, 5) || '20:00';
  const diasUteis = campanha.apenas_dias_uteis ? ', só em dias úteis' : '';

  // "Após a mensagem" respeita a janela permitida; qualquer outro valor segue o padrão do
  // assistente (horário fixo diário), igual ao que createEmptyDraft/draftFromCampaign assumem.
  if (campanha.modo_envio === 'apos_mensagem') {
    return `Envia ${espera} após a última mensagem sem resposta, entre ${inicio} e ${limite}${diasUteis}`;
  }
  return `Todo dia às ${inicio}, para quem está há ${espera} sem responder${diasUteis}`;
};

/**
 * Lista só as restrições que estão LIGADAS.
 * Motivo: mostrar "Anúncios: Não" e "Link personalizado: Não" em todo card enchia a tela de ruído.
 */
export const listarRestricoesFollowup = (
  campanha: Pick<FollowupCampaign, 'apenas_leads_anuncio' | 'ad_name_filtro' | 'apenas_link_personalizado' | 'cooldown_dias_relancamento' | 'limite_diario'>,
): string[] => {
  const restricoes: string[] = [];
  const anuncioEspecifico = campanha.ad_name_filtro?.trim();

  if (anuncioEspecifico) restricoes.push(`Só do anúncio “${anuncioEspecifico}”`);
  else if (campanha.apenas_leads_anuncio) restricoes.push('Só leads de anúncio');
  if (campanha.apenas_link_personalizado) restricoes.push('Só leads de link rastreável');
  if (campanha.cooldown_dias_relancamento > 0) {
    const dias = campanha.cooldown_dias_relancamento;
    restricoes.push(`Pausa de ${dias} ${dias === 1 ? 'dia' : 'dias'} antes de repetir`);
  }
  restricoes.push(`Até ${campanha.limite_diario || 50} envios por dia`);
  return restricoes;
};
